import React, { useState, useRef } from "react";
import { Card, Form, Button, Alert, ProgressBar, Row, Col } from "react-bootstrap";
import { importarGastosExcel } from "../api/gastos.api.ts";

export default function UploadGastosExcel({ anio, onImportSuccess }) {
    const fileInputRef = useRef(null);
    
    const [estado, setEstado] = useState("idle"); // idle, uploading, success, error
    const [archivo, setArchivo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [progreso, setProgreso] = useState(0);
    const [resultado, setResultado] = useState(null);
    const [error, setError] = useState(null);

    // Manejar selección de archivo
    const handleArchivoChange = (e) => {
        const file = e.target.files[0];
        
        if (!file) {
            setArchivo(null);
            return;
        }

        // Validar tipo de archivo
        const tiposValidos = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];

        if (!tiposValidos.includes(file.type)) {
            setError("Por favor selecciona un archivo Excel (.xlsx o .xls)");
            setArchivo(null);
            return;
        }

        // Validar tamaño (máximo 10MB)
        const tamanioMax = 10 * 1024 * 1024;
        if (file.size > tamanioMax) {
            setError("El archivo es demasiado grande. Máximo 10MB.");
            setArchivo(null);
            return;
        }

        setArchivo(file);
        setError(null);
        setEstado("idle");
    };

    // Manejar carga del archivo
    const handleCargar = async () => {
        if (!archivo) {
            setError("Por favor selecciona un archivo");
            return;
        }

        if (!anio) {
            setError("Por favor selecciona un año");
            return;
        }

        try {
            setCargando(true);
            setEstado("uploading");
            setProgreso(0);
            setError(null);
            setResultado(null);

            // Realizar carga usando la función API
            const response = await importarGastosExcel(
                archivo,
                anio,
                (progress) => setProgreso(progress)
            );

            // Procesar respuesta
            if (response && response.summary) {
                setResultado(response.summary);
                setEstado(response.success ? "success" : "error");
                
                // Limpiar archivo si la carga fue exitosa
                if (response.success) {
                    setArchivo(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                    
                    // Llamar callback de éxito
                    //if (onImportSuccess) {
                    onImportSuccess(response.summary);
                    //}
                }
            } else {
                setError(response?.message || "Error desconocido al importar");
                setEstado("error");
            }

        } catch (err) {
            console.error('Error al importar gastos:', err);
            setError(
                err.message || 'Error al importar el archivo'
            );
            setEstado("error");
        } finally {
            setCargando(false);
        }
    };

    // Limpiar estado
    const handleLimpiar = () => {
        setArchivo(null);
        setEstado("idle");
        setProgreso(0);
        setResultado(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Descargar template
    const handleDescargarTemplate = () => {
        // Esta funcionalidad se puede agregar más adelante
        alert('Función de descarga de template disponible pronto');
    };

    return (
        <Card className="mc-card mb-4">
            <Card.Header>
                <h5 className="mb-0">
                    <i className="material-icons me-2">upload_file</i>
                    Importar Gastos desde Excel
                </h5>
            </Card.Header>
            <Card.Body>
                {/* Mensaje de error */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                        <i className="material-icons me-2">error</i>
                        <strong>Error:</strong> {error}
                    </Alert>
                )}

                {/* Resultado exitoso */}
                {estado === "success" && resultado && (
                    <Alert variant="success" dismissible onClose={handleLimpiar} className="mb-3">
                        <div>
                            <h6 className="mb-2">
                                <i className="material-icons me-2">check_circle</i>
                                Importación Completada Exitosamente
                            </h6>
                            <Row className="mt-2">
                                <Col md={3}>
                                    <small className="text-muted">Procesados:</small>
                                    <div className="fs-5 fw-bold">{resultado.totalProcesados}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Insertados:</small>
                                    <div className="fs-5 fw-bold text-success">{resultado.insertados}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Fallidos:</small>
                                    <div className="fs-5 fw-bold text-danger">{resultado.fallidos}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Tasa de Éxito:</small>
                                    <div className="fs-5 fw-bold text-primary">
                                        {resultado.totalProcesados > 0 
                                            ? Math.round((resultado.insertados / resultado.totalProcesados) * 100) 
                                            : 0}%
                                    </div>
                                </Col>
                            </Row>
                            {resultado.erroresDetalle && resultado.erroresDetalle.length > 0 && (
                                <div className="mt-3">
                                    <strong className="d-block mb-2">Errores encontrados:</strong>
                                    <div style={{maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px'}}>
                                        {resultado.erroresDetalle.map((error, idx) => (
                                            <small key={idx} className="d-block text-danger mb-1">
                                                • {error}
                                            </small>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Alert>
                )}

                {/* Resultado con errores */}
                {estado === "error" && resultado && (
                    <Alert variant="warning" dismissible onClose={handleLimpiar} className="mb-3">
                        <div>
                            <h6 className="mb-2">
                                <i className="material-icons me-2">warning</i>
                                Importación Completada con Errores
                            </h6>
                            <Row className="mt-2">
                                <Col md={3}>
                                    <small className="text-muted">Procesados:</small>
                                    <div className="fs-5 fw-bold">{resultado.totalProcesados}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Insertados:</small>
                                    <div className="fs-5 fw-bold text-success">{resultado.insertados}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Fallidos:</small>
                                    <div className="fs-5 fw-bold text-danger">{resultado.fallidos}</div>
                                </Col>
                                <Col md={3}>
                                    <small className="text-muted">Tasa de Éxito:</small>
                                    <div className="fs-5 fw-bold text-primary">
                                        {resultado.totalProcesados > 0 
                                            ? Math.round((resultado.insertados / resultado.totalProcesados) * 100) 
                                            : 0}%
                                    </div>
                                </Col>
                            </Row>
                            {resultado.erroresDetalle && resultado.erroresDetalle.length > 0 && (
                                <div className="mt-3">
                                    <strong className="d-block mb-2">Errores encontrados:</strong>
                                    <div style={{maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px'}}>
                                        {resultado.erroresDetalle.map((error, idx) => (
                                            <small key={idx} className="d-block text-danger mb-1">
                                                • {error}
                                            </small>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Alert>
                )}

                {/* Área de carga */}
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Seleccionar Archivo Excel</Form.Label>
                            <Form.Control
                                type="file"
                                ref={fileInputRef}
                                onChange={handleArchivoChange}
                                accept=".xlsx,.xls"
                                disabled={cargando}
                                className={archivo ? 'is-valid' : ''}
                            />
                            <Form.Text className="text-muted d-block mt-2">
                                Formatos soportados: .xlsx, .xls (Máximo 10MB)
                            </Form.Text>
                            {archivo && (
                                <Form.Text className="d-block mt-2 text-success">
                                    <i className="material-icons me-1" style={{fontSize: '16px', verticalAlign: 'middle'}}>check_circle</i>
                                    Archivo seleccionado: {archivo.name}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Año de Presupuesto</Form.Label>
                            <Form.Control
                                type="number"
                                value={anio}
                                disabled
                                className="bg-light"
                            />
                            <Form.Text className="text-muted d-block mt-2">
                                Selecciona el año en los filtros arriba
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Barra de progreso */}
                {cargando && (
                    <div className="mb-3">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="small fw-bold">Subiendo archivo...</span>
                            <span className="small fw-bold">{progreso}%</span>
                        </div>
                        <ProgressBar 
                            now={progreso} 
                            animated 
                            striped 
                            variant="primary"
                        />
                    </div>
                )}

                {/* Botones de acción */}
                <div className="d-flex gap-2 justify-content-end">
                    <Button 
                        variant="outline-secondary" 
                        onClick={handleDescargarTemplate}
                        disabled={cargando}
                    >
                        <i className="material-icons me-1">download</i>
                        Descargar Template
                    </Button>
                    <Button 
                        variant="outline-danger" 
                        onClick={handleLimpiar}
                        disabled={cargando || (!archivo && estado === "idle")}
                    >
                        <i className="material-icons me-1">clear</i>
                        Limpiar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleCargar}
                        disabled={!archivo || cargando || !anio}
                    >
                        <i className="material-icons me-1">cloud_upload</i>
                        {cargando ? 'Cargando...' : 'Cargar Gastos'}
                    </Button>
                </div>

                {/* Información */}
                <div className="mt-4 p-3 bg-light rounded">
                    <h6 className="mb-2">
                        <i className="material-icons me-1">info</i>
                        Información Importante
                    </h6>
                    <ul className="mb-0 small text-muted">
                        <li>El archivo debe tener los encabezados: MONTO, REFERENCIA, FECHA, CATEGORIA, SUBCATEGORIA</li>
                        <li>Todos los gastos se crearán con estado "Pendiente" y requieren aprobación</li>
                        <li>Los códigos de gasto se generan automáticamente</li>
                        <li>Las categorías y subcategorías deben existir en el sistema</li>
                        <li>Se generará un reporte con los errores encontrados</li>
                    </ul>
                </div>
            </Card.Body>
        </Card>
    );
}
